import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi, { parseError } from "util/api"

import {
  AdminBonus2,
  AdminBonus2Simple,
  AdminPlayerBonus2,
  BetValuesFailure,
  BetValuesResult,
  BonusAnalyticsResponse,
  BonusBanner,
  BonusDeleteResult,
  BonusHistory,
  BonusSaveResult,
  BonusViolationList,
  BulkAssignResult,
  FreeSpinsStats,
  GameBetValues,
  GameContribution,
  GameGroup,
  PaginatedBonusResponse,
  PromoCodeBonus,
  PromoCodeUsage,
  PromoCodeWriteResult,
  StakeValidationIssue,
  TagAudience
} from "./type"

/**
 * SOFT-736 — read a rejected request's body.
 *
 * The axios interceptor rejects with `error.response`, so the backend's JSON sits at `error.data`
 * — one level shallower than the usual `error.response.data`. Getting this wrong silently loses
 * every validation issue, so it is unwrapped in exactly one place.
 */
const errorBody = (
  error: unknown
): { message?: string; details?: PromoCodeErrorDetails & { errors?: StakeValidationIssue[] } } | undefined =>
  (error as { data?: { message?: string; details?: PromoCodeErrorDetails & { errors?: StakeValidationIssue[] } } })?.data

/** The shape the backend uses to name refused promo codes. */
interface PromoCodeErrorDetails {
  conflicting_codes?: string[]
  invalid_codes?: string[]
  max_codes?: number
}

const errorStatus = (error: unknown): number | undefined => (error as { status?: number })?.status

/**
 * A refused save. The issues travel in `details.errors`: this API drops the payload whenever an
 * error is present, and `message` alone carries only the FIRST code — without the game, the
 * currency or the tiers the form needs to offer a fix.
 */
const saveFailure = (error: unknown): BonusSaveResult => {
  const body = errorBody(error)
  const details = body?.details
  const refusedCodes = details?.conflicting_codes || details?.invalid_codes || details?.max_codes
  return {
    ok: false,
    status: errorStatus(error),
    message: body?.message,
    errors: Array.isArray(details?.errors) ? (details?.errors as StakeValidationIssue[]) : [],
    ...(refusedCodes
      ? {
          promoCodes: {
            conflicting: details?.conflicting_codes ?? [],
            invalid: details?.invalid_codes ?? [],
            maxCodes: details?.max_codes,
            message: body?.message
          }
        }
      : {})
  }
}

/**
 * Why a bet-values call failed. A 503 is `BET_VALUES_UNAVAILABLE` — the ladders could not be READ.
 * It must never be rendered as "this game has no tiers": that is the very state the operator is
 * trying to detect. 409 and 400 are the two states that used to hide inside it — a sync already
 * running (wait) and a provider that has no ladders at all (permanent).
 */
const betValuesFailure = (error: unknown): BetValuesFailure => {
  const status = errorStatus(error)
  if (status === 503) return "unavailable"
  if (status === 409) return "in_progress"
  if (status === 404) return "not_found"
  if (status === 400 && errorBody(error)?.message === "BET_VALUES_NOT_SUPPORTED") return "not_supported"
  return "error"
}

/** A code write the backend refused. The offending codes travel in `details`. */
const promoWriteFailure = (error: unknown): PromoCodeWriteResult => {
  const details = errorBody(error)?.details
  return {
    ok: false,
    conflicting: details?.conflicting_codes ?? [],
    invalid: details?.invalid_codes ?? [],
    maxCodes: details?.max_codes,
    message: errorBody(error)?.message
  }
}

/** Deleting a code with redemptions is refused permanently — Archive is the path for those. */
export type PromoCodeDeleteResult = { ok: true } | { ok: false; redeemed: boolean; message?: string }

export const AdminBonus2Service = {
  /**
   * List all bonuses
   */
  async listBonuses(tenant: string, activeOnly = false): Promise<AdminBonus2Simple[] | undefined> {
    try {
      const { data } = await useApi.get<IResponse<{ bonuses: AdminBonus2Simple[] }>>(REQ.ADMIN_DASHBOARD.BONUS2.LIST, {
        params: { tenant, ...(activeOnly && { active: "true" }) }
      })

      if (data?.success) {
        return data.response.bonuses
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Table handler for paginated bonus list
   */
  async getBonusesTable(
    page: number,
    pageSize: number,
    id: string | number | undefined,
    // SOFT-644 — `archived: "true"` flips the table to the Archive partition. Absent (the
    // default) returns the live catalog with archived templates filtered out.
    filters: { q?: string; type?: string; active?: string; tenant?: string; archived?: string } | undefined,
    sortRow?: string,
    sortOrder?: string
  ): Promise<PaginatedBonusResponse> {
    try {
      const { data } = await useApi.get<IResponse<PaginatedBonusResponse>>(REQ.ADMIN_DASHBOARD.BONUS2.TABLE, {
        params: {
          page,
          pageSize,
          sortRow,
          sortOrder,
          ...filters
        }
      })

      if (data?.success) {
        return data.response
      }
      return { data: [], page: 1, pageSize: 10, total: 0, total_pages: 0 }
    } catch (error) {
      parseError(error)
      return { data: [], page: 1, pageSize: 10, total: 0, total_pages: 0 }
    }
  },

  /**
   * Get bonus by ID
   */
  async getBonus(id: string, tenant: string): Promise<AdminBonus2 | undefined> {
    try {
      const { data } = await useApi.get<IResponse<{ bonus: AdminBonus2 }>>(REQ.ADMIN_DASHBOARD.BONUS2.GET.replace(":id", id), {
        params: { tenant }
      })

      if (data?.success) {
        return data.response.bonus
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Create new bonus.
   *
   * SOFT-736 — returns the OUTCOME, not just the bonus. A free-spins save is validated before it
   * is written: a refusal carries per-field issues in `details.errors` and an accepted save may
   * carry non-blocking `warnings`. Swallowing the rejection (the previous `parseError` + return
   * `undefined`) threw both away, leaving the form with nothing to say beyond "failed".
   */
  async createBonus(bonusData: Partial<AdminBonus2>, tenant: string): Promise<BonusSaveResult> {
    try {
      const { data } = await useApi.post<IResponse<{ bonus: AdminBonus2; warnings?: StakeValidationIssue[] }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.CREATE,
        bonusData,
        { params: { tenant } }
      )

      if (data?.success) {
        return { ok: true, bonus: data.response.bonus, warnings: data.response.warnings ?? [] }
      }
      return { ok: false, errors: [] }
    } catch (error) {
      return saveFailure(error)
    }
  },

  /**
   * Update bonus. Same outcome contract as `createBonus` — see there.
   */
  async updateBonus(id: string, bonusData: Partial<AdminBonus2>, tenant: string): Promise<BonusSaveResult> {
    try {
      const { data } = await useApi.put<IResponse<{ bonus: AdminBonus2; warnings?: StakeValidationIssue[] }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.UPDATE.replace(":id", id),
        bonusData,
        {
          params: { tenant }
        }
      )

      if (data?.success) {
        return { ok: true, bonus: data.response.bonus, warnings: data.response.warnings ?? [] }
      }
      return { ok: false, errors: [] }
    } catch (error) {
      return saveFailure(error)
    }
  },

  /**
   * SOFT-736 — a game's per-currency bet ladders: the tiers a free-spins stake must be picked
   * from. Under /admin/bonus2, so PAGE_BONUS2 alone is enough (the bonus form is what needs it).
   */
  async getGameBetValues(gameId: string, tenant: string): Promise<BetValuesResult> {
    try {
      const { data } = await useApi.get<IResponse<GameBetValues>>(REQ.ADMIN_DASHBOARD.BONUS2.GAME_BET_VALUES.replace(":gameId", gameId), {
        params: { tenant }
      })

      if (data?.success) return { ok: true, data: data.response }
      return { ok: false, reason: "error" }
    } catch (error) {
      return { ok: false, reason: betValuesFailure(error) }
    }
  },

  /**
   * SOFT-736 — refetch ONE game's ladders from the provider. `currencies` empty = every fiat
   * currency. Never loop this over a game list: the catalogue-wide sweep it replaced ran for
   * 13h40m and issued ~78.5k failing provider calls.
   */
  async syncGameBetValues(gameId: string, tenant: string, currencies: string[] = []): Promise<BetValuesResult> {
    try {
      const { data } = await useApi.post<IResponse<GameBetValues>>(
        REQ.ADMIN_DASHBOARD.BONUS2.SYNC_GAME_BET_VALUES.replace(":gameId", gameId),
        { currencies },
        { params: { tenant } }
      )

      if (data?.success) return { ok: true, data: data.response }
      return { ok: false, reason: "error" }
    } catch (error) {
      return { ok: false, reason: betValuesFailure(error) }
    }
  },

  /**
   * Permanently delete a bonus template.
   *
   * SOFT-644 — only ever succeeds for a template NOBODY has claimed (a typo, a test row).
   * The moment a player bonus references it the backend refuses with 409 TEMPLATE_IN_USE,
   * because deleting it would take the player's history with it. That refusal is returned
   * as `inUse` rather than toasted generically, so the caller can point the operator at
   * Archive — the action that actually retires a template with history.
   */
  async deleteBonus(id: string, tenant: string): Promise<BonusDeleteResult> {
    try {
      const { data } = await useApi.delete<IResponse<{ bonus: AdminBonus2 }>>(REQ.ADMIN_DASHBOARD.BONUS2.DELETE.replace(":id", id), {
        params: { tenant }
      })

      if (data?.success) return { ok: true }
      return { ok: false, inUse: false }
    } catch (error) {
      // 409 is the referenced-by-player-bonuses refusal. It is NOT toasted here: it is a
      // permanent, actionable state and the caller renders the "archive it instead" advice.
      const inUse = errorStatus(error) === 409
      if (!inUse) parseError(error)
      return { ok: false, inUse, message: errorBody(error)?.message }
    }
  },

  /**   * Reactivate bonus
   */
  async reactivateBonus(id: string, tenant: string): Promise<AdminBonus2 | undefined> {
    try {
      const { data } = await useApi.post<IResponse<{ bonus: AdminBonus2 }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.REACTIVATE.replace(":id", id),
        {},
        { params: { tenant } }
      )

      if (data?.success) {
        return data.response.bonus
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Archive a bonus template (SOFT-644).
   *
   * The retirement path for a template that cannot be deleted because active or historical
   * player bonuses reference it. Nothing is removed — those bonuses are unaffected; the
   * template only leaves the main Bonus Templates list and moves to the Archive tab. The
   * backend also deactivates it, so an archived template is never offered to players.
   */
  async archiveBonus(id: string, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ bonus: { id: string; archived: boolean } }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.ARCHIVE.replace(":id", id),
        {},
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * Restore an archived bonus template to the main list (SOFT-644).
   *
   * The template comes back INACTIVE: putting it back on the operator's list is not the same
   * decision as making it claimable again — that stays an explicit Reactivate.
   */
  async unarchiveBonus(id: string, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ bonus: { id: string; archived: boolean } }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.UNARCHIVE.replace(":id", id),
        {},
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**   * Get player bonuses
   */
  async getPlayerBonuses(userId: string, tenant: string): Promise<AdminPlayerBonus2[] | undefined> {
    try {
      const { data } = await useApi.get<IResponse<{ playerBonuses: AdminPlayerBonus2[] }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PLAYER.LIST.replace(":userId", userId),
        {
          params: { tenant }
        }
      )

      if (data?.success) {
        return data.response.playerBonuses
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Subscribe user to bonus (Admin assigns bonus)
   */
  // `coin` is OPTIONAL and normally omitted: bonus-service denominates the bonus in the
  // PLAYER'S wallet coin (resolved from financial-service). The admin must not pick a currency
  // — a mismatch would activate the bonus in the wrong coin. A value is only a last-resort
  // fallback for when the wallet coin can't be resolved.
  async subscribeUserToBonus(userId: string, bonusId: string, tenant: string, coin?: string): Promise<AdminPlayerBonus2 | undefined> {
    try {
      const { data } = await useApi.post<IResponse<{ playerBonus: AdminPlayerBonus2 }>>(REQ.ADMIN_DASHBOARD.BONUS2.PLAYER.SUBSCRIBE, {
        userId,
        bonusId,
        ...(coin ? { coin } : {}),
        tenant
      })

      if (data?.success) {
        return data.response.playerBonus
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Activate subscribed bonus for user
   */
  async activatePlayerBonus(
    playerBonus: AdminPlayerBonus2,
    depositAmount: number | null,
    tenant: string
  ): Promise<AdminPlayerBonus2 | undefined> {
    try {
      const { data } = await useApi.post<IResponse<{ playerBonus: AdminPlayerBonus2 }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PLAYER.ACTIVATE.replace(":playerBonusId", playerBonus.id),
        {
          userId: playerBonus.user_id,
          bonusId: playerBonus.bonus_id,
          coin: playerBonus.coin,
          depositAmount
        },
        { params: { tenant } }
      )

      if (data?.success) {
        return data.response.playerBonus
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Cancel player bonus
   */
  async cancelPlayerBonus(playerBonus: AdminPlayerBonus2, reason: string, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ success: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PLAYER.CANCEL.replace(":playerBonusId", playerBonus.id),
        { userId: playerBonus.user_id, reason },
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * Get bonus history
   */
  async getBonusHistory(playerBonusId: string, tenant: string): Promise<BonusHistory[] | undefined> {
    try {
      const { data } = await useApi.get<IResponse<{ history: BonusHistory[] }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PLAYER.HISTORY.replace(":playerBonusId", playerBonusId),
        {
          params: { tenant }
        }
      )

      if (data?.success) {
        return data.response.history
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Get violations
   */
  async getViolations(tenant: string, offset = 0): Promise<BonusViolationList | undefined> {
    try {
      const { data } = await useApi.get<IResponse<BonusViolationList>>(REQ.ADMIN_DASHBOARD.BONUS2.VIOLATIONS.LIST, {
        params: { tenant, offset }
      })

      if (data?.success) {
        return {
          violations: data.response.violations ?? [],
          counts: data.response.counts ?? [],
          total: data.response.total ?? 0
        }
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Iteration-3 Fix 01 — soft-dismiss a violation (clears it from the log; kept for audit).
   */
  async dismissViolation(violationId: string, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ dismissed: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.VIOLATIONS.DISMISS.replace(":id", violationId),
        {},
        { params: { tenant } }
      )
      return Boolean(data?.success && data.response.dismissed)
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * Update game contribution rate
   */
  async updateGameContribution(gameType: string, contributionRate: number, tenant: string): Promise<GameContribution | undefined> {
    try {
      const { data } = await useApi.put<IResponse<{ contribution: GameContribution }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.GAME_CONTRIBUTION.UPDATE.replace(":gameType", gameType),
        { contributionRate },
        { params: { tenant } }
      )

      if (data?.success) {
        return data.response.contribution
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * List game groups (Req 6) — reusable named sets of game ids for a tenant.
   */
  async listGameGroups(tenant: string): Promise<GameGroup[] | undefined> {
    try {
      const { data } = await useApi.get<IResponse<{ groups: GameGroup[] }>>(REQ.ADMIN_DASHBOARD.BONUS2.GAME_GROUPS.LIST, {
        params: { tenant }
      })

      if (data?.success) {
        return data.response.groups
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Create or update a game group (Req 6). Empty/omitted id = create.
   */
  async upsertGameGroup(group: { id?: string; name: string; game_ids: string[] }, tenant: string): Promise<GameGroup | undefined> {
    try {
      const { data } = await useApi.post<IResponse<{ group: GameGroup; created: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.GAME_GROUPS.UPSERT,
        group,
        { params: { tenant } }
      )

      if (data?.success) {
        return data.response.group
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Delete a game group (Req 6).
   */
  async deleteGameGroup(groupId: string, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.delete<IResponse<{ group: { id: string; deleted: boolean } }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.GAME_GROUPS.DELETE.replace(":groupId", groupId),
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * List reusable bonus banners (Req 1) for a tenant. `activeOnly` filters to active ones.
   */
  async listBonusBanners(tenant: string, activeOnly = false): Promise<BonusBanner[] | undefined> {
    try {
      const { data } = await useApi.get<IResponse<{ banners: BonusBanner[] }>>(REQ.ADMIN_DASHBOARD.BONUS2.BONUS_BANNERS.LIST, {
        params: { tenant, ...(activeOnly && { active_only: "true" }) }
      })

      if (data?.success) {
        return data.response.banners
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Create or update a reusable bonus banner (Req 1). Empty/omitted id = create.
   */
  async upsertBonusBanner(
    banner: { id?: string; name: string; desktop_url: string; mobile_url?: string; i18n_json?: string; active?: boolean },
    tenant: string
  ): Promise<BonusBanner | undefined> {
    try {
      const { data } = await useApi.post<IResponse<{ banner: BonusBanner; created: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.BONUS_BANNERS.UPSERT,
        banner,
        { params: { tenant } }
      )

      if (data?.success) {
        return data.response.banner
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Delete a reusable bonus banner (Req 1).
   */
  async deleteBonusBanner(bannerId: string, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.delete<IResponse<{ banner: { id: string; deleted: boolean } }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.BONUS_BANNERS.DELETE.replace(":bannerId", bannerId),
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * Get free spins statistics
   */
  async getFreeSpinsStats(
    bonusId: string,
    tenant: string
  ): Promise<
    | {
        bonus: AdminBonus2
        stats: FreeSpinsStats
        playerBonuses: AdminPlayerBonus2[]
      }
    | undefined
  > {
    try {
      const { data } = await useApi.get<
        IResponse<{
          bonus: AdminBonus2
          stats: FreeSpinsStats
          playerBonuses: AdminPlayerBonus2[]
        }>
      >(REQ.ADMIN_DASHBOARD.BONUS2.FREE_SPINS.STATS.replace(":bonusId", bonusId), { params: { tenant } })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Get all active free spins with summary
   */
  async getActiveFreeSpinsWithSummary(tenant: string): Promise<
    | {
        activeFreeSpins: AdminPlayerBonus2[]
        summary: {
          totalPlayers: number
          totalSpinsRemaining: number
          totalWinnings: number
          expiringToday: number
        }
      }
    | undefined
  > {
    try {
      const { data } = await useApi.get<
        IResponse<{
          activeFreeSpins: AdminPlayerBonus2[]
          summary: {
            totalPlayers: number
            totalSpinsRemaining: number
            totalWinnings: number
            expiringToday: number
          }
        }>
      >(REQ.ADMIN_DASHBOARD.BONUS2.FREE_SPINS.ACTIVE, {
        params: { tenant }
      })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Get all active free spins (player bonuses only)
   */
  async getActiveFreeSpins(tenant: string): Promise<AdminPlayerBonus2[] | undefined> {
    const response = await this.getActiveFreeSpinsWithSummary(tenant)
    return response?.activeFreeSpins
  },

  /**
   * Award free spins to player
   */
  /**
   * Award additional free spins to an existing player bonus
   */
  async awardFreeSpins(
    playerBonusId: string,
    spinsCount: number,
    expirationDays: number,
    tenant: string
  ): Promise<AdminPlayerBonus2 | undefined> {
    try {
      const { data } = await useApi.post<IResponse<{ playerBonus: AdminPlayerBonus2 }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.FREE_SPINS.AWARD,
        { playerBonusId, spinsCount, expirationDays, tenant },
        { params: { tenant } }
      )

      if (data?.success) {
        return data.response.playerBonus
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Extend free spins expiration
   */
  async extendFreeSpins(playerBonusId: string, additionalDays: number, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ success: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.FREE_SPINS.EXTEND.replace(":playerBonusId", playerBonusId),
        { additionalDays },
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * Cancel free spins
   */
  async cancelFreeSpins(playerBonusId: string, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ success: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.FREE_SPINS.CANCEL.replace(":playerBonusId", playerBonusId),
        {},
        {
          params: { tenant }
        }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * List promo codes
   */
  async listPromoCodes(tenant: string, includeArchived = false): Promise<PromoCodeBonus[] | undefined> {
    try {
      const { data } = await useApi.get<IResponse<{ promoCodes: PromoCodeBonus[] }>>(REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.LIST, {
        params: { tenant, ...(includeArchived && { include_archived: "true" }) }
      })

      if (data?.success) {
        return data.response.promoCodes
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Validate promo code
   */
  async validatePromoCode(
    code: string,
    tenant: string
  ): Promise<
    | {
        valid: boolean
        promoCode?: PromoCodeBonus
        error?: string
      }
    | undefined
  > {
    try {
      const { data } = await useApi.get<
        IResponse<{
          valid: boolean
          promoCode?: PromoCodeBonus
          error?: string
        }>
      >(REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.VALIDATE.replace(":code", code), { params: { tenant } })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Get promo code usage stats
   */
  async getPromoCodeUsage(
    code: string,
    tenant: string
  ): Promise<
    | {
        bonus: PromoCodeBonus
        usages: PromoCodeUsage[]
        stats?: {
          totalRedemptions: number
          totalBonusAwarded: number
          totalWagered: number
        }
      }
    | undefined
  > {
    try {
      const { data } = await useApi.get<
        IResponse<{
          bonus: PromoCodeBonus
          usages: PromoCodeUsage[]
          stats?: {
            totalRedemptions: number
            totalBonusAwarded: number
            totalWagered: number
          }
        }>
      >(REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.USAGE.replace(":code", code), { params: { tenant } })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Add codes to a bonus. All-or-nothing: on a refusal nothing was written and the result names
   * EVERY offending code, so the editor can mark each row rather than only the first.
   */
  async createPromoCodes(bonusId: string, codes: string[], tenant: string): Promise<PromoCodeWriteResult> {
    try {
      const { data } = await useApi.post<IResponse<{ codes: string[] }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.CREATE,
        { bonus_id: bonusId, codes },
        { params: { tenant } }
      )

      if (data?.success) return { ok: true, codes: data.response.codes }
      return { ok: false, conflicting: [], invalid: [] }
    } catch (error) {
      return promoWriteFailure(error)
    }
  },

  /**
   * Codes that are free right now. They are NOT reserved — the bonus may not exist yet — so a
   * collision is still possible at save time and is reported per code.
   */
  async generatePromoCodes(tenant: string, count: number, length?: number): Promise<string[] | undefined> {
    try {
      const { data } = await useApi.post<IResponse<{ codes: string[] }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.GENERATE,
        { count, ...(length ? { length } : {}) },
        { params: { tenant } }
      )

      if (data?.success) {
        return data.response.codes
      }
    } catch (error) {
      parseError(error)
    }
  },

  /**
   * Switch ONE code on or off. `id` is the code's own id — this leaves the bonus and its other
   * codes untouched.
   */
  async togglePromoCode(id: string, active: boolean, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ success: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.TOGGLE.replace(":id", id),
        { active },
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * Retire a code. The row and its redemptions stay, and the string stays reserved, so a code can
   * never be recycled onto another bonus.
   */
  async archivePromoCode(id: string, archived: boolean, tenant: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<{ success: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.ARCHIVE.replace(":id", id),
        { archived },
        { params: { tenant } }
      )

      return data?.success || false
    } catch (error) {
      parseError(error)
      return false
    }
  },

  /**
   * Permanently remove a code. Only ever succeeds for one nobody redeemed (a typo, a test row);
   * the backend refuses anything with history and the caller points the operator at Archive.
   */
  async deletePromoCode(id: string, tenant: string): Promise<PromoCodeDeleteResult> {
    try {
      const { data } = await useApi.delete<IResponse<{ success: boolean }>>(
        REQ.ADMIN_DASHBOARD.BONUS2.PROMO_CODES.DELETE.replace(":id", id),
        { params: { tenant } }
      )

      if (data?.success) return { ok: true }
      return { ok: false, redeemed: false }
    } catch (error) {
      const redeemed = errorStatus(error) === 409
      if (!redeemed) parseError(error)
      return { ok: false, redeemed, message: errorBody(error)?.message }
    }
  },

  /**
   * Get bonus analytics dashboard data
   */
  async getAnalytics(tenant: string, startDate?: string, endDate?: string): Promise<BonusAnalyticsResponse | undefined> {
    try {
      const { data } = await useApi.get<IResponse<BonusAnalyticsResponse>>(REQ.ADMIN_DASHBOARD.BONUS2.ANALYTICS, {
        params: { tenant, startDate, endDate }
      })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      parseError(error)
    }
  },

  async getTagAudience(tagSlugs: string[], tenant: string): Promise<TagAudience | undefined> {
    try {
      const { data } = await useApi.get<IResponse<TagAudience>>(REQ.ADMIN_DASHBOARD.BONUS2.PLAYER.TAG_AUDIENCE, {
        params: { tenant, tagSlugs: tagSlugs.join(",") }
      })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      parseError(error)
    }
  },

  async subscribeUsersByTag(bonusId: string, tagSlugs: string[], tenant: string): Promise<BulkAssignResult | undefined> {
    try {
      const { data } = await useApi.post<IResponse<BulkAssignResult>>(REQ.ADMIN_DASHBOARD.BONUS2.PLAYER.SUBSCRIBE_BULK, {
        bonusId,
        tagSlugs,
        tenant
      })

      if (data?.success) {
        return data.response
      }
    } catch (error) {
      parseError(error)
    }
  }
}
